import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { tenantsApi } from "@/api/client";
import type { Tenant } from "@/types";

export default function TenantDetail() {
  const { tenantId = "" } = useParams<{ tenantId: string }>();
  const navigate = useNavigate();

  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const t = await tenantsApi.get(tenantId);
      setTenant(t);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [tenantId]);

  useEffect(() => {
    void load();
    // PROVISIONING 상태에선 5초마다 폴링
    const id = setInterval(() => {
      if (tenant?.status === "PROVISIONING" || tenant?.status === "DELETING") {
        void load();
      }
    }, 5_000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, tenant?.status]);

  async function action(label: string, fn: () => Promise<unknown>) {
    if (!confirm(`${label} workspace '${tenantId}'?`)) return;
    setBusy(true);
    setError(null);
    try {
      await fn();
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  if (!tenant && !error) return <p className="muted">Loading...</p>;
  if (error) return <p className="error">{error}</p>;
  if (!tenant) return null;

  return (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
        <Link to="/" className="muted">← Back</Link>
        <h1 style={{ margin: 0 }}>{tenant.displayName}</h1>
        <span className={`badge ${tenant.status}`}>{tenant.status}</span>
      </div>

      <section style={{ background: "white", padding: 24, borderRadius: 8, border: "1px solid var(--color-border)", marginBottom: 16 }}>
        <table>
          <tbody>
            <Row k="Tenant ID" v={tenant.tenantId} />
            <Row k="Domain" v={
              tenant.status === "ACTIVE"
                ? <a href={`https://${tenant.domain}`} target="_blank" rel="noreferrer">{tenant.domain}</a>
                : tenant.domain
            } />
            <Row k="Admin email" v={tenant.adminEmail} />
            <Row k="Plan" v={tenant.planSize} />
            <Row k="Mattermost version" v={tenant.mattermostVersion ?? "—"} />
            <Row k="Created" v={tenant.createdAt} />
            <Row k="Created by" v={tenant.createdBy ?? "—"} />
            {tenant.failedReason && (
              <Row k="Failure reason" v={<code>{tenant.failedReason}</code>} />
            )}
          </tbody>
        </table>
      </section>

      <section style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {tenant.status === "ACTIVE" && (
          <>
            <button onClick={() => void action("Suspend", () => tenantsApi.suspend(tenantId))} disabled={busy}>Suspend</button>
            <button className="danger" onClick={() => void action("Delete (soft, 7-day grace)", async () => {
              await tenantsApi.delete(tenantId);
              navigate("/");
            })} disabled={busy}>Delete</button>
          </>
        )}
        {tenant.status === "SUSPENDED" && (
          <button className="primary" onClick={() => void action("Resume", () => tenantsApi.resume(tenantId))} disabled={busy}>
            Resume
          </button>
        )}
        {tenant.status === "FAILED" && (
          <button className="danger" onClick={() => void action("Force delete", async () => {
            await tenantsApi.delete(tenantId);
            navigate("/");
          })} disabled={busy}>Force delete</button>
        )}
        {tenant.status === "PROVISIONING" && (
          <p className="muted">Provisioning in progress... auto-refreshing every 5s.</p>
        )}
      </section>
    </>
  );
}

function Row({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <tr>
      <th style={{ width: 200 }}>{k}</th>
      <td>{v}</td>
    </tr>
  );
}
